import { Fragment, useEffect, useMemo, useState } from "react";

import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import panelStyles from "../../styles/components/templates/TemplatePreview.module.css";
import Button from "../../ui/buttons/Button";
import cardStyles from "../../ui/cards/TemplateCards.module.css";
import DropdownSelect from "../../ui/dropdowns/DropdownSelect";
import BaseInput from "../../ui/inputs/BaseInput/BaseInput";
import RichTextEditor from "../../ui/inputs/RichTextEditor";
import ModalOverlay from "../../ui/Modals/ModalOverlay";
import { extractApiErrorMessage } from "../../utils/apiError";
import {
  CATEGORY_OPTIONS,
  LANGUAGE_OPTIONS,
  MAX_BUTTONS,
  MAX_BUTTON_TEXT_LENGTH,
  MAX_FOOTER_LENGTH,
  buildTemplatePayload,
  draftFromTemplate,
  emptyDraft,
  fillExamples,
  htmlToWhatsAppText,
  sanitizeTemplateName,
  statusInfo,
  validateDraft,
  variableNames,
  whatsAppLineToHtml,
  whatsAppTextToHtml,
} from "../../utils/whatsappTemplateDraft";
import { ConfirmDeleteModal } from "../database/ContactConfirmModals";

import styles from "./WhatsAppTemplates.module.css";

export const NEW_TEMPLATE_TITLE = "Nueva plantilla de WhatsApp";
export const REVIEW_HINT =
  "Meta suele revisarlas en minutos u horas. Te avisamos con una notificación cuando la aprueben o la rechacen.";
export const DELETE_RULE_HINT =
  "Cada plantilla la puede borrar o editar quien la creó o un administrador de la casilla del número.";
export const EDIT_PENDING_HINT = "Mientras Meta la revisa no se puede editar.";
export const EDIT_UNSUPPORTED_HINT =
  "Esta plantilla tiene partes que no se editan desde acá (encabezado con imagen, video o documento, botones de teléfono o links con variables, u otras partes que MaqMailing no arma): editala en el WhatsApp Manager.";
export const EDIT_REVIEW_NOTE =
  "Al guardar, Meta vuelve a revisar la plantilla: deja de estar aprobada hasta que la apruebe de nuevo y mientras tanto no se puede enviar. Una plantilla aprobada admite hasta 10 ediciones cada 30 días.";
export const EDIT_LABEL = "Editar";
export const SAVE_EDIT_LABEL = "Guardar y enviar a revisión";
export const SUBMIT_LABEL = "Enviar a revisión de Meta";
export const SUBMIT_ERROR_MESSAGE = "No se pudo enviar la plantilla a Meta.";
export const EXAMPLE_REQUIRED_MESSAGE = "Completá un ejemplo: Meta lo usa para revisar la plantilla.";
export const CONTENT_PLACEHOLDER =
  "Te escribimos de {{company_name}} por la cotización que pediste. ¿Seguís interesado?";
export const EMPTY_PREVIEW_TEXT = "Tu mensaje va a aparecer acá.";
export const EXTERNAL_CREATOR_TEXT = "creada en WhatsApp Manager";
export const PREVIEW_EXAMPLES_HINT =
  "Los tags se reemplazan por los ejemplos que cargues más abajo; Meta los pide para la revisión.";
export const NO_GROUP_ID = 0;
export const NEW_GROUP_ID = "new";
export const NO_GROUP_LABEL = "Sin grupo";
export const NEW_GROUP_LABEL = "+ Nuevo grupo";
export const NEW_GROUP_PLACEHOLDER = "Nombre del grupo";
export const GROUP_ERROR_MESSAGE = "No se pudo guardar el grupo.";

const FIELD_ERROR_KEYS = {
  name: "name",
  language: "language",
  category: "category",
  header_text: "headerText",
  header_text_examples: "headerTextExamples",
  header_examples: "headerTextExamples",
  body_text: "bodyText",
  body_text_examples: "bodyTextExamples",
  body_examples: "bodyTextExamples",
  footer_text: "footerText",
  buttons: "buttons",
};

const firstMessage = (value) => {
  if (typeof value === "string") return value;
  const nested = Array.isArray(value) ? value : value && typeof value === "object" ? Object.values(value) : [];
  for (const item of nested) {
    const message = firstMessage(item);
    if (message) return message;
  }
  return null;
};

const backendFieldErrors = (requestError) => {
  const data = requestError?.response?.data;
  if (!data || typeof data !== "object") return {};
  return Object.entries(FIELD_ERROR_KEYS).reduce((errors, [backendKey, formKey]) => {
    const message = errors[formKey] ? null : firstMessage(data[backendKey]);
    return message ? { ...errors, [formKey]: message } : errors;
  }, {});
};

export const numberLabel = (connection) => connection.display_phone_number || connection.phone_number_id;

export const connectionLabel = (connection) =>
  `${numberLabel(connection)}${connection.verified_name ? ` · ${connection.verified_name}` : ""}`;

export const accountNames = (connections = []) => [
  ...new Set(connections.map((connection) => connection.mail_account_name || connection.mail_account_email).filter(Boolean)),
];

export const languageLabel = (code) =>
  LANGUAGE_OPTIONS.find((option) => option.value === code)?.label || code;

export const categoryLabel = (code) =>
  CATEGORY_OPTIONS.find((option) => option.value === (code || "").toUpperCase())?.label ||
  (code === "AUTHENTICATION" ? "Autenticación" : code);

const singleLine = (text) => text.replace(/\s*\n+\s*/g, " ").trim();

const stopPropagation = (event) => event.stopPropagation();

const languageItems = LANGUAGE_OPTIONS.map((option) => ({ id: option.value, name: option.label }));
const categoryItems = CATEGORY_OPTIONS.map((option) => ({ id: option.value, name: option.label }));

const selectedLanguageItem = (code) =>
  languageItems.find((item) => item.id === code) || (code ? { id: code, name: languageLabel(code) } : null);

const selectedCategoryItem = (code) =>
  categoryItems.find((item) => item.id === code) || (code ? { id: code, name: categoryLabel(code) } : null);

function NumberTag({ connection }) {
  return (
    <span className={cardStyles.tag}>
      <span className={`${cardStyles.text} ${cardStyles.tagText}`}>{connectionLabel(connection)}</span>
    </span>
  );
}

function FormattedText({ text }) {
  return String(text)
    .split("\n")
    .map((line, index) => (
      <Fragment key={index}>
        {index > 0 && <br />}
        <span dangerouslySetInnerHTML={{ __html: whatsAppLineToHtml(line) }} />
      </Fragment>
    ));
}

function ChatPreview({ header, body, footer, buttons }) {
  return (
    <div className={styles.previewChat}>
      <div className={styles.previewBubble} aria-label="Vista previa de la plantilla">
        {header && <strong className={styles.previewHeader}>{header}</strong>}
        <span className={styles.previewBody}>{body ? <FormattedText text={body} /> : EMPTY_PREVIEW_TEXT}</span>
        {footer && <span className={styles.previewFooter}>{footer}</span>}
      </div>
      {buttons
        .filter((button) => (button.text || "").trim())
        .map((button, index) => (
          <div className={styles.previewButton} key={index}>
            {(button.type || "").toUpperCase() === "URL" ? "↗ " : ""}
            {button.text.trim()}
          </div>
        ))}
    </div>
  );
}

export function GroupField({ groups = [], value = null, onChange, onCreateGroup, disabled = false }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState(null);
  const items = [
    { id: NO_GROUP_ID, name: NO_GROUP_LABEL },
    ...groups,
    ...(onCreateGroup ? [{ id: NEW_GROUP_ID, name: NEW_GROUP_LABEL }] : []),
  ];
  const selected = items.find((item) => item.id === (value ?? NO_GROUP_ID)) || items[0];

  const select = (item) => {
    if (item.id === NEW_GROUP_ID) {
      setCreating(true);
      return;
    }
    onChange?.(item.id === NO_GROUP_ID ? null : item.id);
  };

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const group = await onCreateGroup(name);
      setCreating(false);
      setNewName("");
      setError(null);
      onChange?.(group.id);
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError) || GROUP_ERROR_MESSAGE);
    }
  };

  return (
    <div className={styles.groupField}>
      <DropdownSelect title="Grupo" items={items} selectedItem={selected} onChange={select} isEditing={!disabled} />
      {creating && (
        <div className={styles.groupFieldRow}>
          <BaseInput placeholder={NEW_GROUP_PLACEHOLDER} value={newName} onChange={setNewName} error={error} />
          <Button tone="blue" height={40} onClick={create} disabled={!newName.trim()}>
            Crear
          </Button>
        </div>
      )}
    </div>
  );
}

function TemplateForm({
  template = null,
  mode,
  tags,
  groups,
  defaultGroupId = null,
  connections,
  connection,
  onConnectionChange,
  onCreateGroup,
  onSubmit,
  onUpdate,
  onClose,
  onCancelEdit,
  submitting,
}) {
  const readOnly = mode === "view";
  const creating = mode === "create";
  const editing = mode === "edit";
  const [draft, setDraft] = useState(() => (template ? draftFromTemplate(template) : emptyDraft()));
  const [groupId, setGroupId] = useState(defaultGroupId);
  useEffect(() => {
    if (groupId != null && !groups.some((group) => group.id === groupId)) setGroupId(null);
  }, [groups, groupId]);
  const [headerHtml, setHeaderHtml] = useState(() => whatsAppTextToHtml(template?.header_text));
  const [bodyHtml, setBodyHtml] = useState(() => whatsAppTextToHtml(template?.body_text));
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const labels = useMemo(() => Object.fromEntries(tags.map((tag) => [tag.code, tag.label])), [tags]);
  const editorTags = useMemo(() => tags.map((tag) => ({ label: tag.label, value: tag.code })), [tags]);
  const connectionItems = useMemo(
    () => connections.map((candidate) => ({ id: candidate.id, name: connectionLabel(candidate) })),
    [connections],
  );

  const update = (changes) => {
    setDraft((previous) => ({ ...previous, ...changes }));
    setServerErrors({});
    setSubmitError(null);
  };

  const handleHeaderChange = (html) => {
    setHeaderHtml(html);
    update({ headerText: singleLine(htmlToWhatsAppText(html, { formatting: false })) });
  };

  const handleBodyChange = (html) => {
    setBodyHtml(html);
    update({ bodyText: htmlToWhatsAppText(html) });
  };

  const updateButton = (index, changes) => {
    update({
      buttons: draft.buttons.map((button, position) => (position === index ? { ...button, ...changes } : button)),
    });
  };

  const errors = useMemo(() => ({ ...validateDraft(draft), ...serverErrors }), [draft, serverErrors]);
  const visibleErrors = showErrors ? errors : serverErrors;
  const usedNames = [...new Set([...variableNames(draft.headerText), ...variableNames(draft.bodyText)])];
  const exampleError = (name) =>
    showErrors && !String(draft.examples[name] ?? "").trim() ? EXAMPLE_REQUIRED_MESSAGE : undefined;
  const serverExamplesError = serverErrors.headerTextExamples || serverErrors.bodyTextExamples;

  const submit = async (event) => {
    event.preventDefault();
    if (readOnly) return;
    setShowErrors(true);
    if (Object.keys(validateDraft(draft)).length || submitting) return;
    try {
      if (editing) {
        await onUpdate?.(template, buildTemplatePayload(draft));
      } else {
        await onSubmit?.({ ...buildTemplatePayload(draft), group_id: groupId }, connection);
      }
    } catch (requestError) {
      const fieldErrors = backendFieldErrors(requestError);
      setServerErrors(fieldErrors);
      if (!Object.keys(fieldErrors).length) {
        setSubmitError(extractApiErrorMessage(requestError) || SUBMIT_ERROR_MESSAGE);
      }
    }
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.panelColumns}>
        <div className={styles.panelMain}>
          <div className={panelStyles.asuntoRow}>
            <span className={panelStyles.label} style={{ width: 120, flexShrink: 0 }}>
              Nombre
            </span>
            <div className={panelStyles.asuntoInputWrapper}>
              <BaseInput
                placeholder="Nombre"
                value={draft.name}
                onChange={(value) => update({ name: sanitizeTemplateName(value) })}
                inputClassName={panelStyles.asuntoInput}
                error={visibleErrors.name}
                disabled={!creating}
              />
            </div>
          </div>
          {creating && (
            <span className={styles.hint}>
              Solo lo ven ustedes, para encontrarla. Se escribe en minúsculas y con guiones bajos.
            </span>
          )}

          {creating && connections.length > 1 && (
            <div className={styles.fieldBlock}>
              <DropdownSelect
                title="Número"
                items={connectionItems}
                selectedItem={connectionItems.find((item) => item.id === connection?.id) || null}
                onChange={(item) => onConnectionChange(connections.find((candidate) => candidate.id === item.id))}
                isEditing
              />
              <span className={styles.hint}>
                La plantilla queda en la cuenta de WhatsApp Business de ese número y la comparten todos sus números.
              </span>
            </div>
          )}

          <div className={styles.pairRow}>
            <DropdownSelect
              title="Idioma"
              items={languageItems}
              selectedItem={selectedLanguageItem(draft.language)}
              onChange={(item) => update({ language: item.id })}
              isEditing={creating}
            />
            <DropdownSelect
              title="Categoría"
              items={categoryItems}
              selectedItem={selectedCategoryItem(draft.category)}
              onChange={(item) => update({ category: item.id })}
              isEditing={!readOnly}
            />
          </div>
          <span className={styles.hint}>
            {CATEGORY_OPTIONS.find((option) => option.value === draft.category)?.description}
          </span>

          {creating && (
            <GroupField groups={groups} value={groupId} onChange={setGroupId} onCreateGroup={onCreateGroup} />
          )}

          <div className={styles.fieldBlock}>
            <RichTextEditor
              label="Encabezado (opcional)"
              value={headerHtml}
              onChange={handleHeaderChange}
              templateTags={editorTags}
              toolbar="none"
              minHeight={48}
              placeholder="Hola {{first_name}}"
              readOnly={readOnly}
            />
            {visibleErrors.headerText && <span className={styles.fieldError}>{visibleErrors.headerText}</span>}
          </div>

          <div className={styles.fieldBlock}>
            <RichTextEditor
              label="Contenido"
              value={bodyHtml}
              onChange={handleBodyChange}
              templateTags={editorTags}
              toolbar="whatsapp"
              minHeight={180}
              placeholder={CONTENT_PLACEHOLDER}
              readOnly={readOnly}
            />
            {!readOnly && (
              <span className={styles.hint}>
                Los tags son los mismos que en los templates de correo: al enviar desde una conversación se
                completan solos con los datos del lead y del contacto. El mensaje no puede empezar ni
                terminar con un tag.
              </span>
            )}
            {visibleErrors.bodyText && <span className={styles.fieldError}>{visibleErrors.bodyText}</span>}
          </div>

          {!readOnly && usedNames.length > 0 && (
            <div className={styles.examples}>
              <span className={styles.sectionLabel}>Ejemplos para la revisión de Meta</span>
              <span className={styles.hint}>
                Meta pide un valor de ejemplo por tag para revisar la plantilla. No se envían a nadie.
              </span>
              {usedNames.map((name) => (
                <div className={styles.exampleRow} key={name}>
                  <span className={styles.tagPill}>{labels[name] || `{{${name}}}`}</span>
                  <BaseInput
                    placeholder={`Ejemplo de ${labels[name] || name}`}
                    value={draft.examples[name] ?? ""}
                    onChange={(value) => update({ examples: { ...draft.examples, [name]: value } })}
                    error={exampleError(name)}
                  />
                </div>
              ))}
              {serverExamplesError && <span className={styles.fieldError}>{serverExamplesError}</span>}
            </div>
          )}

          {(!readOnly || draft.footerText) && (
            <div className={panelStyles.asuntoRow}>
              <span className={panelStyles.label} style={{ width: 120, flexShrink: 0 }}>
                Pie (opcional)
              </span>
              <div className={panelStyles.asuntoInputWrapper}>
                <BaseInput
                  placeholder="Pie"
                  value={draft.footerText}
                  onChange={(value) => update({ footerText: value.slice(0, MAX_FOOTER_LENGTH) })}
                  error={visibleErrors.footerText}
                  disabled={readOnly}
                />
              </div>
            </div>
          )}

          {(!readOnly || draft.buttons.length > 0) && (
            <div className={styles.fieldBlock}>
              <div className={styles.buttonsHeader}>
                <span className={styles.sectionLabel}>Botones (opcional)</span>
                {!readOnly && draft.buttons.length < MAX_BUTTONS && (
                  <span className={styles.buttonAdders}>
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() =>
                        update({ buttons: [...draft.buttons, { type: "QUICK_REPLY", text: "", url: "" }] })
                      }
                    >
                      + Respuesta rápida
                    </button>
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => update({ buttons: [...draft.buttons, { type: "URL", text: "", url: "" }] })}
                    >
                      + Link
                    </button>
                  </span>
                )}
              </div>
              {draft.buttons.map((button, index) => (
                <div className={styles.buttonRow} key={index}>
                  <BaseInput
                    placeholder={`Texto del botón ${index + 1}`}
                    value={button.text}
                    onChange={(value) => updateButton(index, { text: value.slice(0, MAX_BUTTON_TEXT_LENGTH) })}
                    disabled={readOnly}
                  />
                  {button.type === "URL" && (
                    <BaseInput
                      placeholder={`Link del botón ${index + 1}`}
                      value={button.url}
                      onChange={(value) => updateButton(index, { url: value })}
                      disabled={readOnly}
                    />
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => update({ buttons: draft.buttons.filter((_, position) => position !== index) })}
                      aria-label={`Quitar botón ${index + 1}`}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {visibleErrors.buttons && <span className={styles.fieldError}>{visibleErrors.buttons}</span>}
            </div>
          )}

          {submitError && (
            <p className={styles.error} role="alert">
              {submitError}
            </p>
          )}
        </div>

        <aside className={styles.panelPreview}>
          <span className={styles.previewLabel}>{readOnly ? "Así la ve el contacto" : "Así la va a ver el contacto"}</span>
          {!readOnly && usedNames.length > 0 && <span className={styles.hint}>{PREVIEW_EXAMPLES_HINT}</span>}
          <ChatPreview
            header={fillExamples(draft.headerText.trim(), draft.examples)}
            body={fillExamples(draft.bodyText.trim(), draft.examples)}
            footer={draft.footerText.trim()}
            buttons={draft.buttons}
          />
        </aside>
      </div>

      {!readOnly && (
        <div className={styles.formActions}>
          <Button
            variant="secondary"
            tone="blue"
            height={48}
            onClick={editing ? onCancelEdit : onClose}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button type="submit" variant="primary" tone="blue" height={48} loading={submitting}>
            {editing ? SAVE_EDIT_LABEL : SUBMIT_LABEL}
          </Button>
        </div>
      )}
    </form>
  );
}

const editBlockHint = (template, status) => {
  if (!template.can_delete || template.can_edit) return null;
  return status.tone === "pending" ? EDIT_PENDING_HINT : EDIT_UNSUPPORTED_HINT;
};

const WhatsAppTemplatePanel = ({
  connections = [],
  defaultConnection = null,
  template = null,
  tagsByAccount = {},
  groups = [],
  defaultGroupId = null,
  onClose,
  onSubmit,
  onUpdate,
  onDelete,
  onSetGroup,
  onCreateGroup,
  submitting = false,
  deleting = false,
}) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [mode, setMode] = useState(template ? "view" : "create");
  const [formVersion, setFormVersion] = useState(0);
  const [connection, setConnection] = useState(defaultConnection ?? connections[0] ?? null);
  const accountId = connection?.account ?? null;
  const tags = tagsByAccount[accountId] ?? [];
  const accountGroups = groups.filter((group) => group.accountId === accountId);
  const createGroupForConnection = onCreateGroup ? (name) => onCreateGroup(name, connection) : undefined;
  const status = template ? statusInfo(template.status) : null;
  const numbers = template ? template.numbers || [] : connection ? [connection] : [];
  const details = template
    ? [
        accountNames(numbers).join(", ") || null,
        languageLabel(template.language),
        categoryLabel(template.category),
        template.created_by ? `creada por ${template.created_by}` : EXTERNAL_CREATOR_TEXT,
      ].filter(Boolean)
    : [];

  const cancelEdit = () => {
    setMode("view");
    setFormVersion((version) => version + 1);
  };

  return (
    <ModalOverlay className={panelStyles.ModalOverlay} onClose={onClose}>
      <div className={`${panelStyles.modal} ${styles.wideModal}`} onClick={stopPropagation}>
        <div className={panelStyles.modalContent}>
          {template?.can_delete && mode === "view" && (
            <button
              type="button"
              className={panelStyles.trashBtn}
              aria-label="Eliminar plantilla"
              onClick={() => setConfirmingDelete(true)}
            >
              <TrashIcon className={panelStyles.trashIcon} />
            </button>
          )}

          <div className={`${panelStyles.templateTagId} ${status ? styles[`status_${status.tone}`] : ""}`}>
            {status ? status.label : "Nueva"}
          </div>

          <h2 className={panelStyles.title}>{template ? template.name : NEW_TEMPLATE_TITLE}</h2>

          <div className={panelStyles.AvatarTagGroup}>
            {numbers.map((number) => (
              <NumberTag connection={number} key={number.id} />
            ))}
            {template && <span className={styles.metaRow}>{details.join(" · ")}</span>}
          </div>

          {template && (
            <div className={styles.templateNotes}>
              {mode === "edit" ? (
                <p className={styles.warning} role="note">
                  {EDIT_REVIEW_NOTE}
                </p>
              ) : (
                <>
                  {template.rejected_reason && (
                    <p className={styles.reason} role="alert">
                      Motivo de Meta: {template.rejected_reason}
                    </p>
                  )}
                  {status.tone === "pending" && <p className={styles.hint}>{REVIEW_HINT}</p>}
                  {editBlockHint(template, status) && (
                    <p className={styles.hint}>{editBlockHint(template, status)}</p>
                  )}
                  {!template.can_delete && <p className={styles.hint}>{DELETE_RULE_HINT}</p>}
                </>
              )}
            </div>
          )}

          {template && (
            <GroupField
              groups={accountGroups}
              value={template.group?.id ?? null}
              onChange={(groupId) => onSetGroup?.(template, groupId)}
              onCreateGroup={createGroupForConnection}
            />
          )}

          {template && mode === "view" && template.can_edit && (
            <div className={styles.viewActions}>
              <Button variant="secondary" tone="blue" height={40} onClick={() => setMode("edit")}>
                {EDIT_LABEL}
              </Button>
            </div>
          )}

          <TemplateForm
            key={template ? `${template.name}-${template.language}-${formVersion}` : "new"}
            template={template}
            mode={mode}
            tags={tags}
            groups={accountGroups}
            defaultGroupId={defaultGroupId}
            connections={connections}
            connection={connection}
            onConnectionChange={setConnection}
            onCreateGroup={createGroupForConnection}
            onSubmit={onSubmit}
            onUpdate={onUpdate}
            onClose={onClose}
            onCancelEdit={cancelEdit}
            submitting={submitting}
          />
        </div>
      </div>

      {confirmingDelete && template && (
        <ConfirmDeleteModal
          name={template.name}
          title="¿Borrar la plantilla?"
          description={
            <>
              Meta borra <strong>{template.name}</strong> en todos sus idiomas y no deja volver a usar
              ese nombre por un tiempo.
            </>
          }
          deleting={deleting}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => onDelete?.(template)}
        />
      )}
    </ModalOverlay>
  );
};

export default WhatsAppTemplatePanel;
